// src/components/auth/AuthBranding.tsx
import React from "react";
import { motion, Variants } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { LOGO_URL } from "@/constants";
import { useTranslation } from "react-i18next";

export type AuthView = "login" | "register" | "forgot_password" | "custom"; // Aggiunto 'custom'

interface AuthBrandingProps {
    view: AuthView;
    onChangeView?: (view: AuthView) => void; // reso opzionale
    onLogoClick?: () => void;

    // NUOVE PROPS PER CONTENUTO PERSONALIZZATO
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    footerText?: string;
    footerAction?: {
        label: string;
        onClick: () => void;
    };
}

const textVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: "easeOut" },
    },
    exit: {
        opacity: 0,
        y: -20,
        transition: { duration: 0.3 },
    },
};

const AuthBranding: React.FC<AuthBrandingProps> = ({
    view,
    onChangeView,
    onLogoClick,
    title,
    subtitle,
    footerText,
    footerAction
}) => {
    const { t } = useTranslation();

    // Funzione helper per cambiare view se la prop esiste
    const handleChangeView = (newView: AuthView) => {
        if (onChangeView) onChangeView(newView);
    };

    return (
        <>
            {/* MOBILE HEADER */}
            <div className="lg:hidden w-full h-16 bg-[#0F1115] flex items-center px-6 shrink-0 z-20">
                <img
                    src={LOGO_URL}
                    alt="Clip Logo"
                    className="h-7 w-auto object-contain cursor-pointer active:opacity-80 transition-opacity"
                    onClick={() => onLogoClick?.()}
                />
            </div>

            {/* DESKTOP LEFT PANEL */}
            <div className="hidden lg:flex lg:w-1/2 bg-[#0F1115] relative flex-col px-12 xl:px-24 text-white overflow-hidden transition-all duration-500 min-h-screen">

                {/* Blobs */}
                <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] bg-clip-600/20 rounded-full blur-[120px] pointer-events-none animate-pulse duration-[4s] z-0" />
                <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-orange-500/10 rounded-full blur-[100px] pointer-events-none animate-pulse duration-[6s] z-0" />

                {/* --- LOGO CLICKABILE --- */}
                <div
                    className="absolute top-12 left-12 xl:left-24 z-[100] w-fit h-auto cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => onLogoClick?.()}
                >
                    <img
                        src={LOGO_URL}
                        alt="Clip Logo"
                        className="h-10 w-auto object-contain"
                        style={{ pointerEvents: 'none' }}
                    />
                </div>

                {/* Text content */}
                <div className="flex-1 flex flex-col justify-center relative z-20">
                    <div className="max-w-xl w-full">
                        {/* Come in Auth.tsx: senza AnimatePresence, perché con framer-motion
                            da CDN l'uscita non si completa e il pannello resta indietro
                            rispetto al form mostrato a destra. */}
                        <>

                            {/* --- CUSTOM VIEW (Per NewShop, ecc) --- */}
                            {view === "custom" && (
                                <motion.div
                                    key="custom-text"
                                    variants={textVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    className="space-y-6"
                                >
                                    <h1 className="text-5xl xl:text-6xl font-bold tracking-tight leading-tight">
                                        {title}
                                    </h1>
                                    <div className="text-2xl text-gray-300 font-light leading-relaxed max-w-md">
                                        {subtitle}
                                    </div>

                                    {(footerText || footerAction) && (
                                        <div className="pt-12 border-t border-white/10 w-full">
                                            {footerText && (
                                                <p className="text-gray-400 mb-4 text-lg">
                                                    {footerText}
                                                </p>
                                            )}
                                            {footerAction && (
                                                <button
                                                    onClick={footerAction.onClick}
                                                    className="group flex items-center gap-3 text-white text-lg font-bold hover:text-clip-500 transition-colors relative z-50"
                                                >
                                                    {footerAction.label}
                                                    <ArrowRight
                                                        size={20}
                                                        className="group-hover:translate-x-1 transition-transform"
                                                    />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </motion.div>
                            )}

                            {/* --- LOGIN VIEW --- */}
                            {view === "login" && (
                                <motion.div
                                    key="login-text"
                                    variants={textVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    className="space-y-6"
                                >
                                    <h1 className="text-5xl xl:text-6xl font-bold tracking-tight leading-tight">
                                        {t('auth.branding.login.title_prefix')}<span className="text-clip-500">{t('auth.branding.login.title_highlight')}</span>
                                    </h1>
                                    <p className="text-2xl text-gray-300 font-light leading-relaxed max-w-md">
                                        {t('auth.branding.login.subtitle_prefix')}
                                        <span className="text-clip-500 font-medium">{t('auth.branding.login.subtitle_highlight')}</span>.
                                    </p>

                                    <div className="pt-12 border-t border-white/10 w-full">
                                        <p className="text-gray-400 mb-4 text-lg">
                                            {t('auth.branding.login.footer_text')}
                                        </p>
                                        <button
                                            onClick={() => handleChangeView("register")}
                                            className="group flex items-center gap-3 text-white text-lg font-bold hover:text-clip-500 transition-colors relative z-50"
                                        >
                                            {t('auth.branding.login.footer_btn')}
                                            <ArrowRight
                                                size={20}
                                                className="group-hover:translate-x-1 transition-transform"
                                            />
                                        </button>
                                    </div>
                                </motion.div>
                            )}

                            {/* --- REGISTER VIEW --- */}
                            {view === "register" && (
                                <motion.div
                                    key="register-text"
                                    variants={textVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    className="space-y-6"
                                >
                                    <h1 className="text-5xl xl:text-6xl font-bold tracking-tight leading-tight">
                                        <span className="text-clip-500">{t('auth.branding.register.title_highlight')}</span>!
                                    </h1>
                                    <p className="text-2xl text-gray-300 font-light leading-relaxed max-w-md">
                                        {t('auth.branding.register.subtitle')}
                                    </p>

                                    <div className="pt-12 border-t border-white/10 w-full">
                                        <p className="text-gray-400 mb-4 text-lg">
                                            {t('auth.branding.register.footer_text')}
                                        </p>
                                        <button
                                            onClick={() => handleChangeView("login")}
                                            className="group flex items-center gap-3 text-white text-lg font-bold hover:text-clip-500 transition-colors relative z-50"
                                        >
                                            <ArrowLeft
                                                size={20}
                                                className="group-hover:-translate-x-1 transition-transform"
                                            />
                                            {t('auth.branding.register.footer_btn')}
                                        </button>
                                    </div>
                                </motion.div>
                            )}

                            {/* --- FORGOT PASSWORD VIEW --- */}
                            {view === "forgot_password" && (
                                <motion.div
                                    key="forgot-text"
                                    variants={textVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                    className="space-y-6"
                                >
                                    <h1 className="text-5xl xl:text-6xl font-bold tracking-tight leading-tight">
                                        {t('auth.branding.forgot.title_prefix')}<span className="text-clip-500">{t('auth.branding.forgot.title_highlight')}</span>.
                                    </h1>
                                    <p className="text-2xl text-gray-300 font-light leading-relaxed max-w-md">
                                        {t('auth.branding.forgot.subtitle')}
                                    </p>

                                    <div className="pt-12 border-t border-white/10 w-full">
                                        <button
                                            onClick={() => handleChangeView("login")}
                                            className="group flex items-center gap-3 text-white text-lg font-bold hover:text-clip-500 transition-colors relative z-50"
                                        >
                                            <ArrowLeft
                                                size={20}
                                                className="group-hover:-translate-x-1 transition-transform"
                                            />
                                            {t('auth.branding.forgot.footer_btn')}
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AuthBranding;